import { TabContext, TabList, TabPanel } from '@mui/lab';
import { type Theme, styled, useTheme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { useEffect, useRef, useState } from 'react';
import { CarouselControls } from '#components/CarouselControls/index.tsx';
import { TileSkeleton } from '#components/Tile/Skeleton.tsx';
import { TabTile } from '#components/Tile/TabTile.tsx';
import { type RenderTabImage, type TabItem } from '#components/Tile/types.ts';
import { type ActivePreview } from '#types.js';
import { type RenderTabPanel } from './types.ts';

const SkeletonContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  borderRadius: theme.spacing(0.5),
  [theme.breakpoints.between('md', 'lg')]: {
    paddingBottom: '50%',
  },
  [theme.breakpoints.down('md')]: {
    paddingBottom: '56%',
  },
  paddingBottom: '45%',
  width: '100%',
}));

export type TabsCarouselProps = {
  ariaLabel?: string;
  className: string;
  imageType?: 'backdrop' | 'poster';
  renderTabImage: RenderTabImage;
  renderTabPanel: RenderTabPanel;
  setActivePreview?: (item: ActivePreview) => void;
  tabSx?: SystemStyleObject<Theme>;
  tabs?: TabItem[];
};

export const TabsCarousel = ({
  ariaLabel,
  className,
  renderTabImage,
  renderTabPanel,
  setActivePreview,
  tabSx,
  tabs = [],
}: TabsCarouselProps) => {
  const theme = useTheme();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const carouselContainerRef = useRef<HTMLDivElement | null>(null);
  const carouselScrollRef = useRef<HTMLDivElement | null>(null);
  const activeTab = tabs[activeTabIndex];

  useEffect(() => {
    const container = document.querySelector(`.${className} .MuiTabs-scroller`);
    const scrollable = document.querySelector(`.${className} .MuiTabs-flexContainer`);

    if (!container || !scrollable) {
      return;
    }

    // querySelector just returns Element type and has no generic option
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    carouselContainerRef.current = container as HTMLDivElement;
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    carouselScrollRef.current = scrollable as HTMLDivElement;
  }, [className]);

  return (
    <div className={className}>
      <TabContext value={activeTabIndex}>
        <TabPanel
          sx={{
            [theme.breakpoints.down('sm')]: {
              display: 'none',
            },
            display: 'flex',
            justifyContent: 'center',
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
            [theme.breakpoints.down('md')]: {
              paddingBottom: theme.spacing(1.5),
            },
          }}
          value={activeTabIndex}
        >
          {activeTab ? renderTabPanel(activeTab) : <SkeletonContainer />}
        </TabPanel>
        <TabList
          aria-label={ariaLabel}
          onChange={(_, newActiveTabIndex: number) => {
            setActiveTabIndex(newActiveTabIndex);
          }}
          scrollButtons={false}
          sx={{
            '.MuiTabs-flexContainer': {
              display: 'inline-flex',
              paddingLeft: '10px',
            },
            '.MuiTabs-indicator': {
              display: 'none',
            },
            '.MuiTabs-scroller': {
              paddingTop: '10px',
            },
          }}
          textColor="inherit"
          variant="scrollable"
          visibleScrollbar
        >
          {tabs.length > 0
            ? tabs.map(tab => (
                <TabTile
                  key={tab.id}
                  renderTabImage={renderTabImage}
                  setActivePreview={setActivePreview}
                  sx={tabSx}
                  tab={tab}
                />
              ))
            : Array.from({ length: 5 }).map((_, index) => (
                <TileSkeleton
                  imageType="backdrop"
                  key={index}
                  orientation="vertical"
                  sx={{ ...tabSx, marginRight: theme.spacing(2) }}
                />
              ))}
        </TabList>
      </TabContext>
      <CarouselControls containerRef={carouselContainerRef} scrollRef={carouselScrollRef} />
    </div>
  );
};
